import { TeamInLeague, Player, TacticalMentality, TacticalPosition } from '../domain/entities';
import { FORMATIONS, POSITION_MAP } from '../domain/constants';
import { calculateMatchPerformance, calculatePositionalPenalty } from '../utils/utilities';

export interface ZoneStrengths {
    attack: number;
    mid: number;
    def: number;
}

const applyTacticalModifiers = (strengths: ZoneStrengths, team: TeamInLeague): ZoneStrengths => {
    const newStrengths = { ...strengths };

    // Mentality modifiers (same as match simulation)
    switch (team.tactics.mentality) {
        case 'Attacking':
            newStrengths.attack *= 1.1;
            newStrengths.mid *= 1.05;
            newStrengths.def *= 0.9;
            break;
        case 'Defensive':
            newStrengths.attack *= 0.9;
            newStrengths.mid *= 0.95;
            newStrengths.def *= 1.1;
            break;
    }

    // Defensive style modifiers (same as match simulation)
    switch (team.tactics.defensiveStyle) {
        case 'High Press':
            newStrengths.def *= 1.05;
            newStrengths.mid *= 1.05;
            break;
        case 'Deep Block':
            newStrengths.def *= 1.1;
            newStrengths.attack *= 0.95;
            break;
    }

    return newStrengths;
};

export const calculateZoneStrengths = (team: TeamInLeague, startingXI_Ids: number[]): ZoneStrengths => {
    const startingXI = team.squad.filter(p => startingXI_Ids.includes(p.id));

    if (startingXI.length === 0) {
        return applyTacticalModifiers({ attack: team.baseSkill, mid: team.baseSkill, def: team.baseSkill }, team);
    }

    // Build a map of playerId to tactical position for positional penalty calculation
    const formationPositions = FORMATIONS[team.tactics.formation] || [];
    const playerPositions = new Map<number, TacticalPosition>();
    formationPositions.forEach(tacticalPos => {
        const playerId = team.tactics.lineup[tacticalPos];
        if (playerId !== null) {
            playerPositions.set(playerId, tacticalPos);
        }
    });

    const strengths: Record<string, number> = { attack: 0, mid: 0, def: 0 };
    const counts: Record<string, number> = { attack: 0, mid: 0, def: 0 };
    let hasGk = false;

    startingXI.forEach(player => {
        // Use the same logic as match simulation: calculateMatchPerformance + positional penalty
        const basePerf = calculateMatchPerformance(player);
        const tacticalPos = playerPositions.get(player.id);
        const positionalPenalty = tacticalPos ? calculatePositionalPenalty(player, tacticalPos) : 0;
        const effectivePerformance = basePerf * (1 - positionalPenalty);

        // Determine zone based on actual position in formation
        const generalPos = tacticalPos ? POSITION_MAP[tacticalPos] : player.position;

        if (generalPos === 'GK') {
            strengths.def += effectivePerformance;
            counts.def++;
            hasGk = true;
        } else if (generalPos === 'DEF') {
            strengths.def += effectivePerformance;
            counts.def++;
        } else if (generalPos === 'MID') {
            strengths.mid += effectivePerformance;
            counts.mid++;
        } else if (generalPos === 'FWD') {
            strengths.attack += effectivePerformance;
            counts.attack++;
        }
    });

    const finalStrengths: ZoneStrengths = {
        attack: counts.attack > 0 ? strengths.attack / counts.attack : team.baseSkill * 0.9,
        mid: counts.mid > 0 ? strengths.mid / counts.mid : team.baseSkill,
        def: counts.def > 0 ? strengths.def / counts.def : team.baseSkill * 0.9,
    };

    // Penalize heavily for no GK (same as match simulation)
    if (!hasGk) {
        finalStrengths.def /= 2;
    }

    return applyTacticalModifiers(finalStrengths, team);
};
